import apiClient from '../../../lib/api-client';
import {
  BatchRow,
  ClassOption,
  FilterOptions,
  OverviewData,
  Paged,
  ReportFilters,
  TargetRow,
  UsedCodeRow,
  UserRow,
} from '../types';

const API_BASE = '/activation-report';

interface ListOpts {
  export?: boolean;
}

type FilterKey = keyof ReportFilters;

const COMMON_PARAMS: FilterKey[] = ['from', 'to', 'school_id', 'class_id', 'book_id', 'collection_id', 'batch_id'];
const PAGE_PARAMS: FilterKey[] = ['page', 'per_page'];

export type ReportEndpoint =
  | 'overview'
  | 'by-target'
  | 'by-batch'
  | 'used-codes'
  | 'by-user'
  | 'filter-options'
  | 'class-options';

// Tham số mỗi endpoint của API thực sự đọc
const ENDPOINT_PARAMS: Record<ReportEndpoint, FilterKey[]> = {
  overview: [...COMMON_PARAMS, 'group_by'],
  'by-target': [...COMMON_PARAMS, 'type', ...PAGE_PARAMS],
  'by-batch': [...COMMON_PARAMS, ...PAGE_PARAMS],
  'used-codes': [...COMMON_PARAMS, ...PAGE_PARAMS],
  'by-user': [...COMMON_PARAMS, 'q', ...PAGE_PARAMS],
  'filter-options': [],
  'class-options': ['school_id'],
};

// Dùng chung cho request và queryKey: đổi tham số endpoint không đọc (vd. q ở tổng quan) thì không gọi lại API.
// Bỏ giá trị rỗng để không gửi `school_id=` làm API lọc nhầm; giữ thứ tự khoá của bộ lọc.
export function pickParams(endpoint: ReportEndpoint, f: ReportFilters): ReportFilters {
  const allowed = ENDPOINT_PARAMS[endpoint];
  const picked: Record<string, unknown> = {};
  (Object.keys(f) as FilterKey[]).forEach((key) => {
    const value = f[key];
    if (!allowed.includes(key) || value === undefined || value === null || value === '') return;
    picked[key] = value;
  });
  return picked as ReportFilters;
}

export function buildParams(endpoint: ReportEndpoint, f: ReportFilters, opts: ListOpts = {}): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(pickParams(endpoint, f)).forEach(([key, value]) => params.append(key, String(value)));
  if (opts.export) params.append('export', '1');
  return params;
}

// Không có message từ API (lỗi mạng, proxy trả HTML, 5xx rỗng): hiện lỗi tiếng Việt thay vì
// để lọt chuỗi kỹ thuật của axios ("Network Error", "Request failed with status code 502")
const fallbackMessage = (status?: number) =>
  status
    ? `Không tải được báo cáo (máy chủ trả lỗi ${status}), hãy thử lại`
    : 'Không tải được báo cáo, kiểm tra kết nối mạng rồi thử lại';

// Để toast hiện đúng message của API (vd. 422 "Thu hẹp từ khoá tìm kiếm"); giữ status để hook không thử lại lỗi 4xx
async function get<T>(path: ReportEndpoint, f: ReportFilters = {}, opts?: ListOpts): Promise<T> {
  const qs = buildParams(path, f, opts).toString();
  try {
    const response = await apiClient.get(qs ? `${API_BASE}/${path}?${qs}` : `${API_BASE}/${path}`);
    return response.data.data;
  } catch (error: any) {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    if (typeof message === 'string' && message) throw Object.assign(new Error(message), { status });
    // Giữ lỗi gốc ở `cause` để còn tra cứu được khi debug
    throw Object.assign(new Error(fallbackMessage(status)), { status, cause: error });
  }
}

export const activationReportService = {
  overview: (f: ReportFilters) => get<OverviewData>('overview', f),
  byTarget: (f: ReportFilters, opts?: ListOpts) => get<Paged<TargetRow>>('by-target', f, opts),
  byBatch: (f: ReportFilters, opts?: ListOpts) => get<Paged<BatchRow>>('by-batch', f, opts),
  usedCodes: (f: ReportFilters, opts?: ListOpts) => get<Paged<UsedCodeRow>>('used-codes', f, opts),
  byUser: (f: ReportFilters, opts?: ListOpts) => get<Paged<UserRow>>('by-user', f, opts),
  filterOptions: () => get<FilterOptions>('filter-options'),

  async classOptions(schoolId: string): Promise<ClassOption[]> {
    const data = await get<{ classes: ClassOption[] }>('class-options', { school_id: schoolId });
    return data.classes;
  },
};
